import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
} from "@mui/material";
import {
  FadeInContainer,
  SimpleTextBox,
} from "../../../../components/AtomicDesign/Atoms";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ProductComponent } from "../../../../components/AtomicDesign/Molecules/OrderAccordion/ProductComponent";
import { useBRLmoney } from "../../../../hooks/useBRLmoney";
import { useDateTime } from "../../../../hooks/useDateTime";
import { colors } from "../../../../theme/colors";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBreakpoints } from "../../../../hooks/useBreakpoins";

export function OrderCard({
  order,
  payStatus,
}: {
  order: OrderData;
  payStatus: String;
}) {
  const nav = useNavigate();
  const { BRLmoney } = useBRLmoney();
  const { formatedDate } = useDateTime();
  const [seconds, setSeconds] = useState(30);
  const { isSmalldesktop, isMobile } = useBreakpoints();

  useEffect(() => {
    let interval = undefined;
    if (order) {
      interval = setInterval(
        () =>
          setSeconds((old) => {
            if (seconds <= 0) return 0;
            else return old - 1;
          }),
        1000
      );
      if (seconds === 0) {
        nav("/user/my-info");
        // setSeconds(30);
      }
    } else {
      setSeconds(30);
    }

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [order, seconds]);

  const status = {
    in_process: "Em processamento",
    approved: "Aprovado",
    pending: "Pendente",
  };

  return (
    <Box width={"100%"}>
      <FadeInContainer>
        <Box
          display={"flex"}
          justifyContent={"center"}
          flexDirection={"column"}
          alignItems={"center"}
          width={"100%"}
          sx={{
            border: "1px dotted #b5821b",
            background: "#ffab000f",
            borderRadius: 2,
            mb: 3,
            pt: 1,
            pb: 1,
          }}
        >
          <Typography
            variant="h1"
            textAlign={"center"}
            mb={3}
            color={"#b5821b"}
          >
            Seu pedido foi criado, você será direcionado a tela de pedidos em{" "}
            {seconds} segundos
          </Typography>
          <Typography variant="h1" textAlign={"center"} color={"#b5821b"}>
            A situação do seu pagamento é:{" "}
            {status[payStatus as keyof typeof status]}
          </Typography>
        </Box>

        <Accordion
          sx={{
            width: "100%",
            background: colors.purple[700],
          }}
          expanded
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fill: "#FFFFFF" }} />}
            aria-controls="panel1-content"
            id="panel1-header"
            sx={{ border: "1px solid #FFFFFF" }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: [2, 4],
              }}
            >
              <SimpleTextBox title="N° pedido" description={order?.id} />
              <SimpleTextBox
                title="Data da compra"
                description={formatedDate(order?.createdAt)}
              />
              <SimpleTextBox
                title="Total pago"
                description={BRLmoney(
                  order?.ordersXproducts?.reduce((acc, next) => {
                    return acc + next?.boughtPrice * next?.quantity;
                  }, 0)
                )}
              />
              <SimpleTextBox
                title="Método de pagamento"
                description={
                  order?.paymentType === "ticket"
                    ? "Boleto"
                    : order?.paymentType
                }
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ background: "#FFFFFF" }}>
            <Box
              display={"flex"}
              flexWrap={isSmalldesktop ? "wrap" : "nowrap"}
              width={"100%"}
            >
              <ProductComponent
                selectedOrdersXproducts={order?.ordersXproducts}
                changeQuantityToCancel={() => {}}
              />
              <Box
                sx={{
                  borderRadius: "15px",
                  mt: 1,
                  display: "flex",
                  flexDirection: isSmalldesktop && !isMobile ? "row" : "column",
                  justifyContent: isSmalldesktop
                    ? "space-between"
                    : "flex-start",
                  gap: 2,
                  width: isSmalldesktop ? "100%" : "auto",
                }}
              >
                <Box>
                  <Box>
                    <Typography
                      fontSize={21}
                      fontWeight={700}
                      color={colors.purple[500]}
                      m="10px 0"
                    >
                      Endereço de entrega
                    </Typography>
                    <>
                      <Typography>
                        {order?.address?.address + ", "}
                        {order?.address?.adressNumber}
                      </Typography>
                      {order?.address?.addressComplement && (
                        <Typography>
                          Complemento: {order?.address?.addressComplement}
                        </Typography>
                      )}
                      <Typography>
                        Bairro {order?.address?.neighborhood}
                      </Typography>
                      <Typography>
                        {order?.address?.city + ", "} {order?.address?.country}
                      </Typography>
                      <Typography>CEP: {order?.address?.postalCode}</Typography>
                      <Typography>{order?.address?.receiver}</Typography>
                    </>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mt: 1,
                      mb: 1,
                    }}
                  >
                    <Typography
                      fontSize={18}
                      fontWeight={700}
                      color={colors.purple[500]}
                    >
                      Código de rastreio:
                    </Typography>
                    <Typography fontSize={15}>
                      {order?.trackingCode
                        ? order?.trackingCode
                        : "Aguardando envio"}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  gap={1}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    <Typography
                      fontSize={21}
                      fontWeight={700}
                      color={colors.purple[500]}
                    >
                      Quantidade de itens:
                    </Typography>
                    <Typography fontSize={21}>
                      {order?.ordersXproducts?.reduce(
                        (acc, next) => acc + next?.quantity,
                        0
                      )}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      fontSize={25}
                      fontWeight={700}
                      color={colors.purple[500]}
                    >
                      Itens do pedido:
                    </Typography>

                    <Typography fontSize={21}>
                      {BRLmoney(
                        order?.ordersXproducts?.reduce(
                          (acc, next) =>
                            acc + next?.boughtPrice * next?.quantity,
                          0
                        )
                      )}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    <Typography
                      fontSize={25}
                      fontWeight={700}
                      color={colors.purple[500]}
                    >
                      Frete:
                    </Typography>
                    <Typography fontSize={21}>
                      {order?.freteType} - {`R$ ${Number(order?.fretePrice)}`}
                    </Typography>
                  </Box>
                  {order?.paymentType === "ticket" && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      <a
                        href={order?.billetLink}
                        target="_blank"
                        rel="noreferrer"
                        style={{ width: "100%" }}
                      >
                        <Button variant="contained" fullWidth>
                          Baixar boleto
                        </Button>
                      </a>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            {order?.paymentType === "ticket" && (
              <Typography mt={2}>
                Por favor, efetue o pagamento do boleto dentro do prazo para
                evitar que o pedido seja cancelado, caso já tenha pago, é só
                aguardar a confirmação pela instituição bancaria que pode
                acontecer nos próximos 3 dias úteis.
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </FadeInContainer>
    </Box>
  );
}
